import {
  Add,
  AutoAwesomeOutlined,
  CheckCircleOutlineOutlined,
  Close,
  DashboardOutlined,
  HourglassEmpty,
  Menu,
  RequestQuoteOutlined,
  SaveOutlined,
} from '@mui/icons-material'
import { Box, Drawer, IconButton, Stack } from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { hrefFor, type NavigableRouteKey } from '@omnea-app/fe-authz/routeHref'
import { useCanCreateRequest } from '@omnea-app/fe-authz/useCanCreateRequest'
import { useCanAccessRoute } from '@omnea-app/fe-authz/useRouteAccess'
import { InternalLinkButton } from '@omnea-app/fe-routing/InternalLinkButton'
import { isRouteActive } from '@omnea-app/fe-routing/routes/isRouteActive'
import { useGetMyTaskCount } from '@omnea-app/fe-tasks/useTasks'
import { ExpandableSidebarLink } from '../sidebar/ExpandableSidebarLink'
import { SidebarLink } from '../sidebar/SidebarLink'
import { SidebarDivider, SidebarItemWrapper, SidebarList } from '../sidebar/StyledSidebarComponents'

export const MobileMenu = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { asPath } = useRouter()
  const { t } = useTranslation('common')

  const { data: taskCount = 0 } = useGetMyTaskCount()
  const canAccessAi = useCanAccessRoute('ai')
  const canAccessRequestsList = useCanAccessRoute('requestsList')
  const canAccessAgreementsList = useCanAccessRoute('agreementsList')
  const canCreateRequest = useCanCreateRequest()

  const handleClose = () => setIsOpen(false)

  const isSelected = (route: NavigableRouteKey) => isRouteActive(asPath, hrefFor(route))

  return (
    <>
      <IconButton onClick={() => setIsOpen(!isOpen)} aria-label={t('sidebar.open-menu')}>
        <Menu />
      </IconButton>
      <Drawer
        open={isOpen}
        onClose={handleClose}
        slotProps={{ paper: { sx: { border: 'none', borderRadius: 0 } } }}
        anchor="left"
      >
        <Stack sx={{ minWidth: 'min(70vw, 300px)', height: '100%' }}>
          <Stack direction="row" justifyContent="space-between" py={2} px={4}>
            <Stack component={Link} href="/" passHref direction="row" alignItems="center">
              <Image src="/logo-dark.svg" alt="Omnea" height={18} width={99} priority />
            </Stack>

            <IconButton onClick={() => setIsOpen(!isOpen)} aria-label={t('sidebar.close-menu')}>
              <Close />
            </IconButton>
          </Stack>

          <SidebarDivider />
          <SidebarList>
            <SidebarLink
              href={hrefFor('dashboard')}
              title={t('sidebar.dashboard')}
              selected={isSelected('dashboard')}
              Icon={DashboardOutlined}
              isSidebarExpanded={true}
            />

            {canAccessAi ? (
              <SidebarLink
                href={hrefFor('ai')}
                title={t('sidebar.omnea-ai')}
                selected={isSelected('ai')}
                Icon={AutoAwesomeOutlined}
                isSidebarExpanded={true}
              />
            ) : null}

            <SidebarLink
              href={hrefFor('tasks')}
              title={t('sidebar.tasks')}
              selected={isSelected('tasks')}
              Icon={CheckCircleOutlineOutlined}
              isSidebarExpanded={true}
              count={taskCount}
            />

            {canCreateRequest ? (
              <SidebarLink
                href={hrefFor('startDrafts')}
                title={t('sidebar.drafts')}
                selected={isSelected('startDrafts')}
                Icon={SaveOutlined}
                isSidebarExpanded={true}
              />
            ) : null}

            {canAccessRequestsList ? (
              <SidebarLink
                href={hrefFor('requestsList')}
                title={t('sidebar.resources-requests')}
                selected={isSelected('requestsList')}
                Icon={HourglassEmpty}
                isSidebarExpanded={true}
              />
            ) : null}
            {canAccessAgreementsList ? (
              <SidebarLink
                href={hrefFor('agreementsList')}
                title={t('sidebar.resources-agreements')}
                selected={isSelected('agreementsList')}
                Icon={RequestQuoteOutlined}
                isSidebarExpanded={true}
                onClick={handleClose}
              />
            ) : null}
            <ExpandableSidebarLink
              items={[
                {
                  title: t('sidebar.notifications'),
                  href: hrefFor('settingsNotifications'),
                  selected: isSelected('settingsNotifications'),
                },
                {
                  title: t('sidebar.preferences'),
                  href: hrefFor('settingsPreferences'),
                  selected: isSelected('settingsPreferences'),
                },
              ]}
              isSidebarExpanded={true}
              title={t('sidebar.settings')}
              Icon={DashboardOutlined}
              selected={isRouteActive(asPath, '/settings')}
              onItemClick={handleClose}
            />
          </SidebarList>

          {canCreateRequest ? (
            <Box mt="auto">
              <SidebarList>
                <SidebarItemWrapper>
                  <InternalLinkButton
                    variant="contained"
                    href={hrefFor('startRequest')}
                    fullWidth
                    startIcon={<Add />}
                    color="primary"
                    onClick={handleClose}
                  >
                    {t('sidebar.create-request')}
                  </InternalLinkButton>
                </SidebarItemWrapper>
              </SidebarList>
            </Box>
          ) : null}
        </Stack>
      </Drawer>
    </>
  )
}
